import { Link, Outlet, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Shield, LayoutDashboard, ShoppingBag, LogOut, ArrowLeft, FileText, MapPin, Settings } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

const ACCOUNT_NAV = [
  { label: 'Dashboard', path: '/account', icon: LayoutDashboard },
  { label: 'Orders', path: '/account/orders', icon: ShoppingBag },
  { label: 'Documents', path: '/account/documents', icon: FileText },
  { label: 'Addresses', path: '/account/addresses', icon: MapPin },
  { label: 'Settings', path: '/account/settings', icon: Settings },
];

export default function AccountLayout() {
  const { isAuthenticated, loading, user, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'hsl(var(--bg-paper))' }}>
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/account/sign-in" replace />;
  }

  return (
    <div className="min-h-screen" style={{ background: 'hsl(var(--bg-paper))' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-stone">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 no-underline" data-testid="account-nav-logo">
              <Shield className="h-5 w-5 text-ink" strokeWidth={1.5} />
              <span className="font-display text-base font-medium text-ink hidden sm:inline">The Fashion Detective</span>
            </Link>
            <Separator orientation="vertical" className="h-6" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">My Account</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} className="gap-1.5 text-xs" data-testid="account-logout-btn">
              <LogOut className="h-3.5 w-3.5" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6">
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
          {/* Sidebar nav */}
          <nav className="flex md:flex-col gap-1">
            <Link to="/" className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground no-underline hover:text-ink rounded-md">
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to site
            </Link>
            {ACCOUNT_NAV.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md no-underline transition-colors duration-200 ${
                    active ? 'bg-secondary text-ink' : 'text-muted-foreground hover:text-ink hover:bg-secondary/60'
                  }`}
                  data-testid={`account-nav-${item.label.toLowerCase()}`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Content */}
          <div className="min-h-[60vh]">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
